
import { computed, inject, ref } from 'vue';
import { useContext } from '@nuxtjs/composition-api';
import { NodeType } from '@amplify/amp-module';
import { useStoreInNodes } from '@/composables/useStoreInNodes';
import { CopyNodes, CutNodes, SafelyDeleteNodesById } from '@/constants/actions';
import { PLUGIN_TOOL, TOOLBAR_TOOLS } from '@/constants/amp';
import { SetCurrentNode, SetSelectedSubNode } from '@/constants/mutations';
import { GetChildNodesByParentId, GetNodeById } from '~/constants/getters';
import { CopiedToClipboard } from '~/constants/notifications';

export default {
  props: {
    nodeId: { type: String, default: '' },
    nodeType: { type: String, default: '' },
    nodeIndex: { type: Number, default: -1 },
    selected: { type: Boolean, default: false },
    hovered: { type: Boolean, default: false },
    readonly: { type: Boolean, default: false },
    title: { type: String, default: '' },
    subNodeId: { type: String, default: '' },
  },

  setup(props, { emit }) {
    const { store } = useStoreInNodes();
    const { $notification } = useContext();

    const getGraph = inject('getGraph', () => {});

    const showMenu = ref(false);

    const showActions = computed(() => props.hovered && !props.selected && !props.readonly);

    const nodeDetails = computed(() => [...TOOLBAR_TOOLS, PLUGIN_TOOL].find((tool) => tool.name === props.nodeType));

    const nodeTitle = computed(() => {
      if (props.nodeType === 'broken') {
        return 'Broken node';
      }

      const title =
        (props.nodeIndex >= 0 ? nodeDetails.value?.subNodeLabel : nodeDetails.value?.nodeLabel) || props.nodeType;

      return props.nodeIndex >= 0 ? [title, props.nodeIndex + 1].join(' ') : title;
    });

    const nodeIcon = computed(() => {
      if (props.nodeType === 'broken') {
        return 'warning';
      }

      return nodeDetails.value?.icon || props.nodeType;
    });

    const nodeHasChildren = computed(() => {
      const nodes = store.value.getters[`builderStore/${GetChildNodesByParentId}`]({
        parentId: props.subNodeId ? `${props.nodeId}_port_${props.subNodeId}` : props.nodeId,
      });

      return !!nodes.length;
    });

    const parentNodeHasChildren = computed(() => {
      const nodes = store.value.getters[`builderStore/${GetChildNodesByParentId}`]({ parentId: props.nodeId });

      return !!nodes.length;
    });

    const showNodeChildrenActions = computed(
      () =>
        nodeHasChildren.value ||
        // we need to display the node and its children actions for carousel with one sub node
        (props.nodeType === NodeType.carousel && Object.keys(parentNode.value.subNodes || {}).length === 1)
    );

    const parentNode = computed(() => store.value.getters[`builderStore/${GetNodeById}`]({ id: props.nodeId }));

    const isGroupNode = computed(() => [NodeType.carousel, NodeType.buttonGroup].includes(props.nodeType));

    const showGroupActions = computed(
      () => isGroupNode.value && Object.keys(parentNode.value.subNodes || {}).length > 1
    );

    const groupHasChildren = computed(() => {
      if (!isGroupNode.value) {
        return false;
      }

      const nodes = store.value.getters[`builderStore/${GetChildNodesByParentId}`]({
        parentId: props.nodeId,
      });

      return !!nodes.length;
    });

    const canCutNode = computed(() => {
      if (props.nodeType === NodeType.buttonGroup) {
        return Object.keys(parentNode.value.subNodes || {}).length === 1 || !nodeHasChildren.value;
      }

      if ([NodeType.condition, NodeType.question].includes(props.nodeType)) {
        return !nodeHasChildren.value;
      }

      return true;
    });

    const canCutGroup = computed(() => {
      if (props.nodeType === NodeType.buttonGroup) {
        return !parentNodeHasChildren.value;
      }

      return true;
    });

    const onDelete = ({ deleteChildren = false, deleteGroup = false }) => {
      const nodeToDelete = {
        nodeId: props.nodeId,
        ...(!deleteGroup && props.subNodeId && { subNodeId: props.subNodeId }),
      };

      store.value.dispatch(`builderStore/${SafelyDeleteNodesById}`, { nodes: [nodeToDelete], deleteChildren });
      store.value.commit(`builderStore/${SetSelectedSubNode}`, -1);
      showMenu.value = false;
    };

    const onClose = () => {
      emit('close-node');
      getGraph().cleanSelection();

      if (props.subNodeId) {
        store.value.commit(`builderStore/${SetSelectedSubNode}`, -1);
      }

      store.value.commit(`previewStore/${SetCurrentNode}`, '');
    };

    const onCopy = ({ copyChildren = false, copyGroup = false }) => {
      $notification.toast({ name: CopiedToClipboard });

      // Note: we need to copy the entire group if there is just only one sub node
      // if subNodeId is null, the action will be applied on the entire group/node
      store.value.dispatch(`builderStore/${CopyNodes}`, {
        nodeId: props.nodeId,
        subNodeId:
          !copyGroup && props.subNodeId && Object.keys(parentNode.value.subNodes || {}).length > 1
            ? props.subNodeId
            : null,
        copyChildren,
      });
      showMenu.value = false;
    };

    const onCut = ({ cutChildren = false, cutGroup = false }) => {
      $notification.toast({ name: CopiedToClipboard });

      // Note: we need to cut the entire group if there is just only one sub node
      // if subNodeId is null, the action will be applied on the entire group/node
      store.value.dispatch(`builderStore/${CutNodes}`, {
        nodeId: props.nodeId,
        subNodeId:
          !cutGroup && props.subNodeId && Object.keys(parentNode.value.subNodes || {}).length > 1
            ? props.subNodeId
            : null,
        cutChildren,
      });
      store.value.commit(`builderStore/${SetSelectedSubNode}`, -1);
      showMenu.value = false;
    };

    const onClickOutsideMenu = () => (showMenu.value = false);

    const onMore = () => {
      getGraph()?.getCellById(props.nodeId)?.toFront();
      showMenu.value = true;
    };

    return {
      canCutGroup,
      canCutNode,
      groupHasChildren,
      nodeTitle,
      nodeIcon,
      showActions,
      showNodeChildrenActions,
      showGroupActions,
      showMenu,
      onClickOutsideMenu,
      onClose,
      onCopy,
      onCut,
      onDelete,
      onMore,
    };
  },
};
